import { useCallback, useRef, useState } from "react";
import type { ChatCallError } from "@/features/intelligence/api/client";
import type { PendingAttachment } from "@/features/intelligence/lib/attachments";
import type { ApprovalDeliveryOutcome } from "@/features/intelligence/lib/approval-frame";
import type {
  FrontendCallRecord,
  FrontendToolHost,
} from "@/features/intelligence/lib/frontend-tool-run";
import type { FrontendToolCapabilities } from "@/features/intelligence/lib/frontend-tools";
import {
  newThreadState,
  type ThreadState,
  type TurnOutcome,
} from "@/features/intelligence/lib/thread-state";
import { useApprovalController } from "./chat-surface/use-approval-controller";
import { useAttachmentController } from "./chat-surface/use-attachment-controller";
import { useChatActions } from "./chat-surface/use-chat-actions";
import { useConversationLifecycle } from "./chat-surface/use-conversation-lifecycle";
import { useDocumentHealing } from "./chat-surface/use-document-healing";
import { useFrontendToolController } from "./chat-surface/use-frontend-tool-controller";
import { useStreamExecution } from "./chat-surface/use-stream-execution";
import type { LoadState } from "./chat-surface/types";

export interface ChatSurfaceApi {
  activeId: string | null;
  thread: ThreadState;
  threadState: LoadState;
  threadError: ChatCallError | null;

  busy: boolean;
  actionError: ChatCallError | null;
  approvalOutcome: ApprovalDeliveryOutcome | null;
  canReconnect: boolean;
  answeringApproval: boolean;
  frontendCalls: ReadonlyMap<string, FrontendCallRecord>;

  attachments: readonly PendingAttachment[];
  attach(file: File): Promise<boolean>;
  removeAttachment(key: string): void;

  send(text: string): Promise<TurnOutcome | null>;
  stop(): Promise<void>;
  regenerate(): Promise<void>;
  answerApproval(approved: boolean, reason?: string): Promise<void>;
  reconnect(): Promise<void>;
  dismissApprovalOutcome(): void;
}

export function useChatSurface(
  locale: "sv" | "en",
  conversationId: string | null,
  onConversationCreated?: (id: string) => void,
  frontendContext?: object | null,
  frontendTools?: { capabilities: FrontendToolCapabilities; host: FrontendToolHost } | null,
): ChatSurfaceApi {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [thread, setThread] = useState<ThreadState>(() => newThreadState());
  const [threadState, setThreadState] = useState<LoadState>("ready");
  const [threadError, setThreadError] = useState<ChatCallError | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<ChatCallError | null>(null);
  const [canReconnect, setCanReconnect] = useState(false);

  const threadRef = useRef<ThreadState>(thread);
  const activeIdRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const busyRef = useRef(false);

  const applyThread = useCallback((update: (state: ThreadState) => ThreadState) => {
    const next = update(threadRef.current);
    threadRef.current = next;
    setThread(next);
  }, []);

  const frontend = useFrontendToolController({
    threadRef,
    context: frontendContext,
    binding: frontendTools,
  });
  const healing = useDocumentHealing({
    activeIdRef,
    busyRef,
    threadRef,
    frontendContextRef: frontend.contextRef,
    setCanReconnect,
    applyThread,
  });
  const drive = useStreamExecution({
    activeIdRef,
    abortRef,
    busyRef,
    threadRef,
    runnerRef: frontend.runnerRef,
    setBusy,
    setCanReconnect,
    setActionError,
    applyThread,
    healAfterStream: healing.healAfterStream,
    runFrontendTool: frontend.run,
  });
  const approval = useApprovalController({
    activeIdRef,
    busyRef,
    threadRef,
    setActionError,
    applyThread,
    drive,
  });
  const lifecycle = useConversationLifecycle({
    locale,
    conversationId,
    onConversationCreated,
    activeIdRef,
    abortRef,
    threadRef,
    setActiveId,
    setThreadState,
    setThreadError,
    setActionError,
    setCanReconnect,
    applyThread,
    drive,
    resetFrontendTools: frontend.reset,
    resetApprovalOutcome: approval.resetApprovalOutcome,
  });
  const attachments = useAttachmentController({
    activeId,
    activeIdRef,
    ensureConversation: lifecycle.ensureConversation,
  });
  const actions = useChatActions({
    locale,
    activeIdRef,
    busyRef,
    threadRef,
    frontendContextRef: frontend.contextRef,
    ensureConversation: lifecycle.ensureConversation,
    hasUploadsInFlight: attachments.hasUploadsInFlight,
    takeReadyAttachments: attachments.takeReady,
    resolveFrontendAccess: frontend.resolveAccess,
    armFrontendTools: frontend.arm,
    resetFrontendTools: frontend.reset,
    resetApprovalOutcome: approval.resetApprovalOutcome,
    setActionError,
    setThreadError,
    applyThread,
    drive,
  });

  return {
    activeId,
    thread,
    threadState,
    threadError,
    busy,
    actionError,
    approvalOutcome: approval.approvalOutcome,
    canReconnect,
    answeringApproval: approval.answeringApproval,
    frontendCalls: frontend.frontendCalls,
    attachments: attachments.attachments,
    attach: attachments.attach,
    removeAttachment: attachments.removeAttachment,
    send: actions.send,
    stop: actions.stop,
    regenerate: actions.regenerate,
    answerApproval: approval.answerApproval,
    reconnect: actions.reconnect,
    dismissApprovalOutcome: approval.dismissApprovalOutcome,
  };
}
